// Addresses: /<client> the overview, /<client>/<project> its planning,
// /<client>/<project>/estimate its estimate. Page changes happen in place
// (no reload): the current page's parts leave, the next one comes in.
import * as React from "react"
import { leave } from "../toolkit/motion"

export type Tab = "planning" | "estimate"
export type Route = { client: string; project?: string; tab: Tab }

export function parse(path = location.pathname): Route {
    const [client = "", project, tab] = path.split("/").filter(Boolean).map((s) => decodeURIComponent(s).toLowerCase())
    return { client, project, tab: tab === "estimate" ? "estimate" : "planning" }
}
export const href = (r: Route) =>
    "/" + [r.client, r.project, r.project && r.tab === "estimate" ? "estimate" : ""].filter(Boolean).map(encodeURIComponent).join("/")

let listeners: ((r: Route) => void)[] = []
let busy = false

/** go to a page: the parts on screen leave top to bottom, then the new page. */
export async function go(to: Route | string, o: { replace?: boolean; quiet?: boolean } = {}) {
    const path = typeof to === "string" ? to : href(to)
    if (path === location.pathname || busy) return
    busy = true
    // within a project (planning ↔ estimate) the page stays: the tabs animate
    const from = parse(), next = parse(path)
    const samePage = from.client !== "admin" && from.client === next.client && from.project === next.project
    if (!o.quiet && !samePage) {
        const parts = Array.from(document.querySelectorAll<HTMLElement>("main [data-r], main [data-lines], main [data-leave]")).filter((el) => {
            const r = el.getBoundingClientRect()
            return r.bottom > 0 && r.top < innerHeight
        })
        await leave(parts.slice(0, 14), { to: "up", gap: 28, distance: 14 })
    }
    history[o.replace ? "replaceState" : "pushState"](null, "", path)
    if (!samePage) window.scrollTo(0, 0)
    busy = false
    const r = parse()
    for (const l of listeners) l(r)
}

export function useRoute() {
    const [route, setRoute] = React.useState(parse)
    React.useEffect(() => {
        const on = (r: Route) => setRoute(r)
        listeners.push(on)
        const pop = () => setRoute(parse())
        addEventListener("popstate", pop)
        return () => {
            listeners = listeners.filter((l) => l !== on)
            removeEventListener("popstate", pop)
        }
    }, [])
    return route
}

/** a link that goes through go() (and still opens in a new tab with cmd/ctrl) */
export function Link(props: React.AnchorHTMLAttributes<HTMLAnchorElement> & { to: Route; onGo?: () => void }) {
    const { to, onGo, onClick, ...rest } = props
    return (
        <a
            {...rest}
            href={href(to)}
            onClick={(e) => {
                onClick?.(e)
                if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
                e.preventDefault()
                onGo?.()
                go(to)
            }}
        />
    )
}
